import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
    GET_SHOP_SETTINGS, 
    CREATE_SHOP_SETTINGS, 
    UPDATE_SHOP_SETTINGS,
    GET_SHOP_SETTINGS_BY_ID,
    DELETE_SHOP_SETTINGS
} from '@/services/ApiRoutes';
import { get, post, put, del, createWithMultipart, updateWithMultipart } from '@/services/ApiService';

export interface SocialLinks {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
    youtube?: string;
    tiktok?: string;
    whatsapp?: string;
}

export interface ShopSettings {
    _id?: string;
    shopName: string;
    shopLogo: string;
    shopBanner: string;
    shopDescription: string;
    shopEmail: string;
    shopPhoneNumber: string[];
    shopAddress: string;
    shopLocation: string;
    returnPolicy: string;
    shippingPolicy: string;
    privacyPolicy: string;
    termsAndConditions: string;
    socialLinks: SocialLinks;
    featured: boolean;
    createdAt?: string;
    updatedAt?: string;
}

interface ShopSettingsState {
    data: ShopSettings[];
    currentSettings: ShopSettings | null;
    loading: boolean;
    error: string | null;
}

const initialState: ShopSettingsState = {
    data: [],
    currentSettings: null,
    loading: false,
    error: null,
};

// Helper function to normalize response data
const normalizeSettings = (settings: any): ShopSettings => {
    return {
        ...settings,
        _id: settings._id || settings.id,
    };
};

const normalizeSettingsArray = (settingsArray: any[]): ShopSettings[] => {
    return (settingsArray || []).map(normalizeSettings);
};

// Async Thunks
export const fetchAllShopSettings = createAsyncThunk(
    'shopSettings/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const response = await get(GET_SHOP_SETTINGS);
            const data = response.data?.data || response.data || [];
            return normalizeSettingsArray(Array.isArray(data) ? data : []);
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch shop settings');
        }
    }
);

export const fetchShopSettingsById = createAsyncThunk(
    'shopSettings/fetchById',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await get(GET_SHOP_SETTINGS_BY_ID(id));
            const data = response.data?.data || response.data;
            return normalizeSettings(data);
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch shop settings');
        }
    }
);

export const createShopSettingsAction = createAsyncThunk(
    'shopSettings/create',
    async (data: FormData | ShopSettings, { rejectWithValue }) => {
        try {
            let response;
            if (data instanceof FormData) {
                response = await createWithMultipart(CREATE_SHOP_SETTINGS, data);
            } else {
                response = await post(CREATE_SHOP_SETTINGS, data);
            }
            const responseData = response.data?.data || response.data;
            return normalizeSettings(responseData);
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create shop settings');
        }
    }
);

export const updateShopSettingsAction = createAsyncThunk(
    'shopSettings/update',
    async ({ id, data }: { id: string; data: FormData | Partial<ShopSettings> }, { rejectWithValue }) => {
        try {
            let response;
            if (data instanceof FormData) {
                response = await updateWithMultipart(UPDATE_SHOP_SETTINGS(id), data);
            } else {
                response = await put(UPDATE_SHOP_SETTINGS(id), data);
            }
            const responseData = response.data?.data || response.data;
            return normalizeSettings(responseData);
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update shop settings');
        }
    }
);

export const deleteShopSettingsAction = createAsyncThunk(
    'shopSettings/delete',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await del(DELETE_SHOP_SETTINGS(id));
            return id;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete shop settings');
        }
    }
);

const shopSettingsSlice = createSlice({
    name: 'shopSettings',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearCurrentSettings: (state) => {
            state.currentSettings = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch All
        builder
            .addCase(fetchAllShopSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllShopSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload || [];
            })
            .addCase(fetchAllShopSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch By ID
        builder
            .addCase(fetchShopSettingsById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchShopSettingsById.fulfilled, (state, action) => {
                state.loading = false;
                state.currentSettings = action.payload;
            })
            .addCase(fetchShopSettingsById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Create
        builder
            .addCase(createShopSettingsAction.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createShopSettingsAction.fulfilled, (state, action) => {
                state.loading = false;
                state.data.push(action.payload);
                state.currentSettings = action.payload;
            })
            .addCase(createShopSettingsAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Update
        builder
            .addCase(updateShopSettingsAction.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateShopSettingsAction.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.data.findIndex(item => item._id === action.payload._id);
                if (index >= 0) {
                    state.data[index] = action.payload;
                }
                state.currentSettings = action.payload;
            })
            .addCase(updateShopSettingsAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Delete
        builder
            .addCase(deleteShopSettingsAction.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteShopSettingsAction.fulfilled, (state, action) => {
                state.loading = false;
                state.data = state.data.filter(item => item._id !== action.payload);
            })
            .addCase(deleteShopSettingsAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError, clearCurrentSettings } = shopSettingsSlice.actions;
export default shopSettingsSlice.reducer;
